import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, X, Send, Sparkles, Bot, User, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Chatbot() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [userBills, setUserBills] = useState([]);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fetch user bills for context
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token && isOpen) {
      axios.get(`${API}/bills`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => setUserBills(res.data)).catch(() => {});
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const lang = i18n.language;
      const welcomeMessages = {
        en: "Hello! I'm your SHALEM assistant. I can help you with:\n\n• 💰 Your bills and payments\n• 📅 Booking appointments\n• 📄 Generating payment slips (Shovar)\n• 👨‍👩‍👧 Managing your family\n• 🏠 Changing your address\n\nHow can I help you today?",
        he: "שלום! אני העוזר שלך ב-SHALEM. אני יכול לעזור לך עם:\n\n• 💰 החשבונות והתשלומים שלך\n• 📅 קביעת תורים\n• 📄 הפקת שוברי תשלום\n• 👨‍👩‍👧 ניהול המשפחה שלך\n• 🏠 שינוי כתובת\n\nאיך אני יכול לעזור לך היום?",
        fr: "Bonjour ! Je suis votre assistant SHALEM. Je peux vous aider avec :\n\n• 💰 Vos factures et paiements\n• 📅 Prise de rendez-vous\n• 📄 Génération de bons de paiement (Shovar)\n• 👨‍👩‍👧 Gestion de votre famille\n• 🏠 Changement d'adresse\n\nComment puis-je vous aider aujourd'hui ?"
      };
      
      setMessages([{
        id: Date.now(),
        text: welcomeMessages[lang] || welcomeMessages.en,
        sender: 'bot',
        timestamp: new Date()
      }]);
    }
  }, [isOpen, i18n.language]);

  // Smart response system
  const getSmartResponse = (message) => {
    const lang = i18n.language;
    const lowerMessage = message.toLowerCase();
    const unpaidBills = userBills.filter(b => b.status === 'unpaid');
    const totalDebt = unpaidBills.reduce((sum, b) => sum + b.amount, 0);
    
    // Greetings
    if (lowerMessage.match(/^(hi|hello|hey|bonjour|salut|שלום|היי)/)) {
      const responses = {
        en: `Hello! 👋 I see you have ${unpaidBills.length} unpaid bills totaling ₪${totalDebt.toFixed(2)}. Would you like me to help you with payments?`,
        he: `שלום! 👋 אני רואה שיש לך ${unpaidBills.length} חשבונות לתשלום בסך ₪${totalDebt.toFixed(2)}. האם תרצה שאעזור לך עם התשלומים?`,
        fr: `Bonjour ! 👋 Je vois que vous avez ${unpaidBills.length} factures impayées pour un total de ₪${totalDebt.toFixed(2)}. Voulez-vous que je vous aide avec les paiements ?`
      };
      return { text: responses[lang] || responses.en };
    }

    // Bill status inquiry
    if (lowerMessage.match(/(bill|facture|חשבון|how much|combien|כמה|debt|dette|חוב|status|סטטוס)/)) {
      if (unpaidBills.length === 0) {
        const responses = {
          en: "Great news! 🎉 You have no unpaid bills. All your payments are up to date!",
          he: "חדשות טובות! 🎉 אין לך חשבונות לתשלום. כל התשלומים שלך מעודכנים!",
          fr: "Bonne nouvelle ! 🎉 Vous n'avez aucune facture impayée. Tous vos paiements sont à jour !"
        };
        return { text: responses[lang] || responses.en };
      }
      
      const urgentBills = unpaidBills.filter(b => {
        const days = Math.ceil((new Date(b.due_date) - new Date()) / (1000*60*60*24));
        return days <= 7;
      });
      
      let response = {
        en: `📊 **Your Bill Summary:**\n\n• Total unpaid: **₪${totalDebt.toFixed(2)}**\n• Number of bills: **${unpaidBills.length}**\n• Urgent (due within 7 days): **${urgentBills.length}**\n\n`,
        he: `📊 **סיכום החשבונות שלך:**\n\n• סך לתשלום: **₪${totalDebt.toFixed(2)}**\n• מספר חשבונות: **${unpaidBills.length}**\n• דחופים (עד 7 ימים): **${urgentBills.length}**\n\n`,
        fr: `📊 **Résumé de vos factures :**\n\n• Total impayé : **₪${totalDebt.toFixed(2)}**\n• Nombre de factures : **${unpaidBills.length}**\n• Urgentes (sous 7 jours) : **${urgentBills.length}**\n\n`
      };
      
      // Add top 3 bills
      const topBills = unpaidBills.slice(0, 3);
      const billsList = topBills.map(b => `• ${b.provider}: ₪${b.amount.toFixed(2)}`).join('\n');
      response.en += `**Top bills:**\n${billsList}`;
      response.he += `**חשבונות עיקריים:**\n${billsList}`;
      response.fr += `**Principales factures :**\n${billsList}`;
      
      return { 
        text: response[lang] || response.en,
        action: { type: 'navigate', path: '/bills', label: lang === 'he' ? 'צפה בכל החשבונות' : lang === 'fr' ? 'Voir toutes les factures' : 'View all bills' }
      };
    }

    // Payment inquiry
    if (lowerMessage.match(/(pay|payer|לשלם|payment|paiement|תשלום)/)) {
      const responses = {
        en: "💳 **Payment Options:**\n\n1. **Online Payment** - Pay instantly with credit card\n2. **Partial Payment** - Pay any amount you want\n3. **Shovar (Cash)** - Generate a PDF to pay at post office\n\nGo to any bill and click 'Pay Now' to start!",
        he: "💳 **אפשרויות תשלום:**\n\n1. **תשלום מקוון** - שלם מיידית בכרטיס אשראי\n2. **תשלום חלקי** - שלם כל סכום שתרצה\n3. **שובר (מזומן)** - הפק PDF לתשלום בדואר\n\nלך לכל חשבון ולחץ 'שלם עכשיו' כדי להתחיל!",
        fr: "💳 **Options de paiement :**\n\n1. **Paiement en ligne** - Payez instantanément par carte\n2. **Paiement partiel** - Payez le montant souhaité\n3. **Shovar (Espèces)** - Générez un PDF pour payer à la poste\n\nAllez sur n'importe quelle facture et cliquez 'Payer' !"
      };
      return { 
        text: responses[lang] || responses.en,
        action: { type: 'navigate', path: '/bills', label: lang === 'he' ? 'עבור לחשבונות' : lang === 'fr' ? 'Aller aux factures' : 'Go to bills' }
      };
    }

    // Appointment inquiry
    if (lowerMessage.match(/(appointment|rendez-vous|תור|book|réserver|לקבוע|misrad|משרד|office|bureau)/)) {
      const responses = {
        en: "📅 **Book an Appointment:**\n\nYou can book appointments at:\n• 📮 Post Office\n• 🏛️ Ministry of Interior\n• 🚗 Driving License Office\n• 🛂 Passport Office\n• 💰 Tax Authority\n• 🏥 National Insurance\n• 🏢 Municipality\n\nClick the button below to book!",
        he: "📅 **קביעת תור:**\n\nאתה יכול לקבוע תורים ב:\n• 📮 דואר ישראל\n• 🏛️ משרד הפנים\n• 🚗 משרד הרישוי\n• 🛂 משרד הדרכונים\n• 💰 מס הכנסה\n• 🏥 ביטוח לאומי\n• 🏢 עירייה\n\nלחץ על הכפתור למטה לקביעת תור!",
        fr: "📅 **Prendre rendez-vous :**\n\nVous pouvez prendre rendez-vous à :\n• 📮 Bureau de poste\n• 🏛️ Ministère de l'Intérieur\n• 🚗 Bureau des permis\n• 🛂 Bureau des passeports\n• 💰 Autorité fiscale\n• 🏥 Assurance nationale\n• 🏢 Municipalité\n\nCliquez ci-dessous pour réserver !"
      };
      return { 
        text: responses[lang] || responses.en,
        action: { type: 'navigate', path: '/appointments', label: lang === 'he' ? 'קבע תור עכשיו' : lang === 'fr' ? 'Réserver maintenant' : 'Book now' }
      };
    }

    // Shovar inquiry
    if (lowerMessage.match(/(shovar|שובר|cash|espèces|מזומן|pdf|print|הדפס)/)) {
      const responses = {
        en: "📄 **Shovar (Payment Slip):**\n\nA Shovar is a payment voucher you can print and use to pay in cash at:\n• Any post office branch\n• Any bank branch\n• Authorized payment points\n\nTo generate a Shovar:\n1. Go to 'My Bills'\n2. Select a bill\n3. Click 'Generate Shovar (PDF)'\n4. Print and pay!",
        he: "📄 **שובר תשלום:**\n\nשובר הוא מסמך תשלום שאפשר להדפיס ולשלם במזומן ב:\n• כל סניף דואר\n• כל סניף בנק\n• נקודות תשלום מורשות\n\nכדי להפיק שובר:\n1. עבור אל 'החשבונות שלי'\n2. בחר חשבון\n3. לחץ 'הפקת שובר (PDF)'\n4. הדפס ושלם!",
        fr: "📄 **Shovar (Bon de paiement) :**\n\nUn Shovar est un bon de paiement imprimable pour payer en espèces :\n• Dans tout bureau de poste\n• Dans toute banque\n• Aux points de paiement autorisés\n\nPour générer un Shovar :\n1. Allez dans 'Mes factures'\n2. Sélectionnez une facture\n3. Cliquez 'Générer Shovar (PDF)'\n4. Imprimez et payez !"
      };
      return { text: responses[lang] || responses.en };
    }

    // Address change
    if (lowerMessage.match(/(address|adresse|כתובת|move|déménag|עבר|change)/)) {
      const responses = {
        en: "🏠 **Change of Address:**\n\nWith SHALEM, you can update your address once and it will automatically propagate to ALL government agencies:\n\n• Ministry of Interior\n• Municipality\n• Tax Authority\n• National Insurance\n• Utility companies\n• And more!\n\nNo more visiting 10 different offices!",
        he: "🏠 **שינוי כתובת:**\n\nעם SHALEM, אתה יכול לעדכן את הכתובת פעם אחת והיא תופץ אוטומטית לכל הגופים הממשלתיים:\n\n• משרד הפנים\n• עירייה\n• מס הכנסה\n• ביטוח לאומי\n• חברות חשמל ומים\n• ועוד!\n\nבלי לבקר ב-10 משרדים שונים!",
        fr: "🏠 **Changement d'adresse :**\n\nAvec SHALEM, mettez à jour votre adresse une seule fois et elle sera automatiquement transmise à TOUS les organismes :\n\n• Ministère de l'Intérieur\n• Municipalité\n• Autorité fiscale\n• Assurance nationale\n• Compagnies d'électricité/eau\n• Et plus !\n\nPlus besoin de visiter 10 bureaux différents !"
      };
      return { 
        text: responses[lang] || responses.en,
        action: { type: 'navigate', path: '/address', label: lang === 'he' ? 'שנה כתובת' : lang === 'fr' ? 'Changer d\'adresse' : 'Change address' }
      };
    }

    // Children/Family
    if (lowerMessage.match(/(child|enfant|ילד|family|famille|משפחה|school|école|בית ספר)/)) {
      const responses = {
        en: "👨‍👩‍👧 **Family Management:**\n\nAdd your children to manage their school-related bills:\n• Kindergarten fees\n• School tuition\n• After-school activities\n• School trips\n\nGo to Settings → My Children to add them!",
        he: "👨‍👩‍👧 **ניהול משפחה:**\n\nהוסף את ילדיך כדי לנהל את החשבונות שלהם:\n• שכר לימוד גן\n• שכר לימוד בית ספר\n• חוגים\n• טיולים\n\nעבור להגדרות → הילדים שלי כדי להוסיף!",
        fr: "👨‍👩‍👧 **Gestion familiale :**\n\nAjoutez vos enfants pour gérer leurs factures scolaires :\n• Frais de crèche\n• Frais de scolarité\n• Activités extra-scolaires\n• Sorties scolaires\n\nAllez dans Paramètres → Mes enfants !"
      };
      return { 
        text: responses[lang] || responses.en,
        action: { type: 'navigate', path: '/settings', label: lang === 'he' ? 'עבור להגדרות' : lang === 'fr' ? 'Aller aux paramètres' : 'Go to settings' }
      };
    }

    // Thanks
    if (lowerMessage.match(/(thank|merci|תודה|great|super|génial|מעולה)/)) {
      const responses = {
        en: "You're welcome! 😊 Is there anything else I can help you with?",
        he: "בשמחה! 😊 האם יש עוד משהו שאני יכול לעזור בו?",
        fr: "De rien ! 😊 Y a-t-il autre chose que je puisse faire pour vous ?"
      };
      return { text: responses[lang] || responses.en };
    }

    // Default response
    const defaultResponses = {
      en: "I'm here to help! Here's what I can assist you with:\n\n• 💰 **Bills** - Check status, pay, generate Shovar\n• 📅 **Appointments** - Book at government offices\n• 🏠 **Address** - Update everywhere at once\n• 👨‍👩‍👧 **Family** - Manage children's bills\n\nJust ask me anything!",
      he: "אני כאן לעזור! הנה מה שאני יכול לסייע:\n\n• 💰 **חשבונות** - בדיקה, תשלום, הפקת שובר\n• 📅 **תורים** - קביעה במשרדים ממשלתיים\n• 🏠 **כתובת** - עדכון בכל מקום בבת אחת\n• 👨‍👩‍👧 **משפחה** - ניהול חשבונות ילדים\n\nפשוט שאל אותי הכל!",
      fr: "Je suis là pour vous aider ! Voici ce que je peux faire :\n\n• 💰 **Factures** - Vérifier, payer, générer Shovar\n• 📅 **Rendez-vous** - Réserver aux bureaux\n• 🏠 **Adresse** - Mettre à jour partout\n• 👨‍👩‍👧 **Famille** - Gérer les factures enfants\n\nDemandez-moi n'importe quoi !"
    };
    return { text: defaultResponses[lang] || defaultResponses.en };
  };

  const handleSend = () => {
    if (!inputMessage.trim()) return;

    const userMessage = {
      id: Date.now(),
      text: inputMessage,
      sender: 'user',
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, userMessage]);
    const msgText = inputMessage;
    setInputMessage('');
    setIsTyping(true);

    // Try AI first, fallback to smart responses
    const token = localStorage.getItem('token');
    if (token) {
      axios.post(`${API}/chat`, {
        message: msgText,
        session_id: `chat-${Date.now()}`,
        language: i18n.language
      }, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => {
        const botResponse = {
          id: Date.now() + 1,
          text: res.data.response,
          sender: 'bot',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, botResponse]);
        setIsTyping(false);
      }).catch(() => {
        // Fallback to smart responses
        const response = getSmartResponse(msgText);
        const botResponse = {
          id: Date.now() + 1,
          text: response.text,
          sender: 'bot',
          timestamp: new Date(),
          action: response.action
        };
        setMessages(prev => [...prev, botResponse]);
        setIsTyping(false);
      });
    } else {
      setTimeout(() => {
        const response = getSmartResponse(msgText);
        const botResponse = {
          id: Date.now() + 1,
          text: response.text,
          sender: 'bot',
          timestamp: new Date(),
          action: response.action
        };
        setMessages(prev => [...prev, botResponse]);
        setIsTyping(false);
      }, 800);
    }
  };

  const handleAction = (action) => {
    if (action.type === 'navigate') {
      setIsOpen(false);
      navigate(action.path);
    }
  };

  // Quick action buttons
  const quickActions = [
    { label: i18n.language === 'he' ? 'החשבונות שלי' : i18n.language === 'fr' ? 'Mes factures' : 'My bills', query: 'bills' },
    { label: i18n.language === 'he' ? 'קביעת תור' : i18n.language === 'fr' ? 'Rendez-vous' : 'Appointment', query: 'appointment' },
    { label: i18n.language === 'he' ? 'שובר' : 'Shovar', query: 'shovar' },
  ];

  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-24 lg:bottom-6 right-6 z-50 bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4 rounded-full shadow-2xl hover:shadow-blue-500/50 transition-all hover:scale-110"
          data-testid="chatbot-button"
        >
          <MessageCircle className="h-6 w-6" />
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></span>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-24 lg:bottom-6 right-6 z-50 w-[360px] h-[520px] bg-white rounded-3xl shadow-2xl border border-slate-200 flex flex-col animate-scaleIn overflow-hidden" data-testid="chatbot-window">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 p-2.5 rounded-xl">
                <Bot className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-bold flex items-center gap-2">
                  SHALEM AI
                  <Sparkles className="h-4 w-4 text-yellow-300" />
                </h3>
                <p className="text-xs text-blue-100">Always here to help</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="hover:bg-white/20 p-2 rounded-full transition-colors"
              data-testid="close-chatbot"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gradient-to-b from-slate-50 to-white">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'} animate-fadeIn`}
              >
                <div className={`flex items-end gap-2 max-w-[85%] ${message.sender === 'user' ? 'flex-row-reverse' : ''}`}>
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                    message.sender === 'user' ? 'bg-blue-600' : 'bg-gradient-to-br from-blue-500 to-indigo-600'
                  }`}>
                    {message.sender === 'user' ? 
                      <User className="h-4 w-4 text-white" /> : 
                      <Bot className="h-4 w-4 text-white" />
                    }
                  </div>
                  <div className={`p-3 rounded-2xl ${
                    message.sender === 'user'
                      ? 'bg-blue-600 text-white rounded-br-md'
                      : 'bg-white border border-slate-200 text-slate-800 rounded-bl-md shadow-sm'
                  }`}>
                    <p className="text-sm whitespace-pre-line">{message.text}</p>
                    {message.action && (
                      <Button
                        size="sm"
                        onClick={() => handleAction(message.action)}
                        className="mt-2 bg-blue-500 hover:bg-blue-600 text-white text-xs rounded-full px-4"
                      >
                        {message.action.label} →
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
            
            {isTyping && (
              <div className="flex items-center gap-2 animate-fadeIn">
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                  <Bot className="h-4 w-4 text-white" />
                </div>
                <div className="bg-white border border-slate-200 p-3 rounded-2xl rounded-bl-md shadow-sm">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Actions */}
          {messages.length === 1 && (
            <div className="px-4 pb-2 flex gap-2 flex-wrap">
              {quickActions.map((action, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setInputMessage(action.query);
                    setTimeout(() => handleSend(), 100);
                  }}
                  className="text-xs bg-blue-50 text-blue-700 px-3 py-1.5 rounded-full hover:bg-blue-100 transition-colors"
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="p-4 border-t border-slate-100 bg-white">
            <div className="flex gap-2">
              <Input
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                placeholder={t('ask_question')}
                className="flex-1 rounded-full border-slate-200 focus:border-blue-500"
                data-testid="chatbot-input"
              />
              <Button
                onClick={handleSend}
                disabled={!inputMessage.trim() || isTyping}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 rounded-full px-4 shadow-lg shadow-blue-500/25"
                data-testid="send-message"
              >
                {isTyping ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
